import React, { useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import { fetchProfileMe, presignAvatarUpload, saveAvatarKey, uploadToPresignedUrl } from "../../../components/Profile/avatarApi";
import styles from "./ProfilePage.module.css";

type Message = { type: "success" | "error"; text: string } | null;

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[1]?.[0] || "" : "";
  return (a + b).toUpperCase();
}

function extFromFile(file: File): string {
  const name = file.name || "";
  const idx = name.lastIndexOf(".");
  if (idx >= 0 && idx < name.length - 1) return name.slice(idx + 1).toLowerCase();
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "image/jpeg") return "jpg";
  return "jpg";
}

export default function ProfilePage() {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Message>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [isProvider, setIsProvider] = useState(false);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    const idToken = localStorage.getItem("idToken");
    const accessToken = localStorage.getItem("accessToken");
    if (!idToken && !accessToken) {
      window.location.href = "/login";
      return;
    }
    if (!idToken) return;
    try {
      const decoded: any = jwtDecode(idToken);
      const groups = decoded["cognito:groups"] || [];
      setIsProvider(groups.includes("provider") || groups.includes("Provider"));
      setUserId(decoded?.sub || "");
      const name: string =
        [decoded?.given_name, decoded?.family_name].filter(Boolean).join(" ") ||
        decoded?.name ||
        decoded?.email ||
        "";
      setDisplayName(name);
      setEmail(decoded?.email || "");
      const picture = decoded?.picture || decoded?.avatar_url || null;
      if (picture) setAvatarUrl(picture);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const me = await fetchProfileMe();
      if (!alive || !me) return;
      if (me.avatarUrl) setAvatarUrl(me.avatarUrl);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const initials = useMemo(() => initialsFromName(displayName || email || ""), [displayName, email]);
  const dashboardHref = isProvider ? "/dashboard" : "/patient-dashboard";

  const onPickPhoto = () => {
    setMsg(null);
    fileRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      setMsg({ type: "error", text: "Please choose an image file." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMsg({ type: "error", text: "Image too large (max 5MB)." });
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      const extension = extFromFile(file);
      const presign = await presignAvatarUpload({
        contentType: file.type || "application/octet-stream",
        extension,
      });
      await uploadToPresignedUrl({ uploadUrl: presign.uploadUrl, file });
      await saveAvatarKey({ objectKey: presign.objectKey });
      const me = await fetchProfileMe();
      setAvatarUrl(me?.avatarUrl || null);
      setMsg({ type: "success", text: "Profile photo updated." });
    } catch (ex) {
      const text = ex instanceof Error ? ex.message : "Failed to upload image";
      setMsg({ type: "error", text });
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  };

  return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Profile</h1>
            <p className={styles.subtitle}>
              Your photo and the identity details we hold for this account.
            </p>
          </div>

          <div className={styles.actions}>
            <Link to={dashboardHref} className="ui-btn ui-btn--ghost">
              Back to dashboard
            </Link>
            <Link to="/profile/settings" className="ui-btn ui-btn--secondary">
              Settings
            </Link>
            <button
              type="button"
              className="ui-btn ui-btn--danger"
              onClick={signOut}
            >
              Sign out
            </button>
          </div>
        </div>

        <section
          className={`ui-card ui-card--lg ${styles.panel}`}
          aria-label="Profile"
        >
          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Profile photo</h2>
              <p className={styles.sectionHint}>
                Shown across the app and dashboards.
              </p>
            </div>

            <div className={styles.identityRow}>
              <div className={styles.avatar} aria-hidden="true">
                {avatarUrl ? <img src={avatarUrl} alt="" /> : initials || "?"}
              </div>
              <div className={styles.idText}>
                <p className={styles.name}>{displayName || "Account"}</p>
                <p className={styles.email}>{email}</p>
              </div>
            </div>

            <div className={styles.uploadRow}>
              <button
                type="button"
                className="ui-btn ui-btn--secondary"
                onClick={onPickPhoto}
                disabled={busy}
                aria-busy={busy}
              >
                {busy ? "Uploading..." : "Upload new photo"}
              </button>
              <p className="ui-help">PNG, JPG, or WEBP up to 5MB.</p>
            </div>

            <input
              ref={fileRef}
              className={styles.fileInput}
              type="file"
              accept="image/*"
              onChange={onFileChange}
            />

            {msg ? (
              <p
                className={`ui-banner ${
                  msg.type === "error"
                    ? "ui-banner--error"
                    : "ui-banner--success"
                }`}
                role={msg.type === "error" ? "alert" : "status"}
              >
                {msg.text}
              </p>
            ) : null}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Account details</h2>
              <p className={styles.sectionHint}>Basics pulled from your login.</p>
            </div>

            <div>
              <span
                className={`ui-badge ${isProvider ? "" : "ui-badge--neutral"}`}
              >
                {isProvider ? "Provider" : "Patient"}
              </span>
            </div>

            <div className={styles.metaGrid}>
              <div className={styles.field}>
                <span className={styles.label}>Full name</span>
                <div className={styles.value}>{displayName || "Not set"}</div>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Email</span>
                <div className={styles.value}>{email || "Not set"}</div>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>User ID</span>
                <div className={styles.value}>{userId || "Not set"}</div>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Manage</span>
                <div className={styles.value}>
                  <Link to="/profile/settings" className="ui-link">
                    Open settings
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
}
